import React, { useState, useEffect } from 'react';
import { Framework, DashboardData, GRCControl } from '../../types/grc';
import { grcService } from '../../services/grcService';
import { useI18n } from '../../context/I18nContext';
import { EnhancedSoATable } from './EnhancedSoATable';
import { CISScriptViewer } from './CISScriptViewer';
import { PenaltyAlerts } from './PenaltyAlerts';
import { PenaltyControlModal } from './PenaltyControlModal';
import { 
  Shield, CheckCircle, AlertTriangle, XCircle, 
  BarChart2, FileText, AlertOctagon, ClipboardList,
  RefreshCw, Brain, TrendingUp, Users, Terminal, Hand, ExternalLink, Download
} from 'lucide-react';

interface FrameworkDashboardProps {
  framework: Framework;
  onResetWizard: () => void;
  onNavigate?: (section: string, filters?: any) => void;
}

export const FrameworkDashboard: React.FC<FrameworkDashboardProps> = ({
  framework,
  onResetWizard,
  onNavigate
}) => {
  const { t } = useI18n();
  const [data, setData] = useState<DashboardData | null>(null);
  const [controls, setControls] = useState<GRCControl[]>([]);
  const [activeTab, setActiveTab] = useState<'overview' | 'soa' | 'risks' | 'policies' | 'automated' | 'manual'>('overview');
  const [loading, setLoading] = useState(true);
  const [selectedControl, setSelectedControl] = useState<GRCControl | null>(null);
  const [penaltyControl, setPenaltyControl] = useState<GRCControl | null>(null);
  const [hasDownloadableSource, setHasDownloadableSource] = useState(false);

  // Check if this is a CIS framework
  const isCISFramework = framework.framework_type === 'benchmark' || 
    framework.code?.toLowerCase().includes('cis') ||
    framework.name?.toLowerCase().includes('cis');

  useEffect(() => {
    loadDashboard();
  }, [framework.id]);

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const [dashData, soaData, canDownload] = await Promise.all([
        grcService.getFrameworkDashboard(framework.id),
        grcService.getSoA(framework.id),
        grcService.hasDownloadableSource(framework.id)
      ]);
      setData(dashData);
      setControls(soaData || []);
      setHasDownloadableSource(canDownload);
    } catch (e) {
      console.error('Failed to load dashboard:', e);
      setData(null);
      setControls([]);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-gray-200 dark:border-gray-700 border-t-indigo-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12 text-gray-500 dark:text-gray-400">
        Dashboard data not available.
      </div>
    );
  }

  const stats = data.stats || { total: 0, compliance_score: 0 };
  const status = data.status || { applicable_controls: 0, not_applicable_controls: 0, partial_controls: 0 };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-xl">
            🛡️
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{framework.code}</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">{framework.name}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {hasDownloadableSource && (
            <button
              onClick={() => grcService.downloadFramework(framework.id)}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 border border-indigo-300 dark:border-indigo-600 rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-900/30 transition-colors"
              title={t.actions?.download || 'Download source document'}
            >
              <Download className="w-4 h-4" />
              {t.actions?.download || 'Download'}
            </button>
          )}
          <button
            onClick={onResetWizard}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            {t.actions.reset} {t.questionnaire.title}
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div 
          className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border border-gray-200 dark:border-gray-700 cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => onNavigate?.('controls')}
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <ClipboardList className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white">{stats.total}</div>
          <div className="text-sm text-gray-500 dark:text-gray-400">{t.dashboard.totalControls}</div>
        </div>

        <div 
          className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border border-gray-200 dark:border-gray-700 cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => onNavigate?.('controls', { status: 'applicable' })}
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
          </div>
          <div className="text-3xl font-bold text-green-600 dark:text-green-400">{status.applicable_controls}</div>
          <div className="text-sm text-gray-500 dark:text-gray-400">{t.dashboard.applicable}</div>
        </div>

        <div 
          className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border border-gray-200 dark:border-gray-700 cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => onNavigate?.('controls', { status: 'partial' })}
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
          </div>
          <div className="text-3xl font-bold text-amber-600 dark:text-amber-400">{status.partial_controls}</div>
          <div className="text-sm text-gray-500 dark:text-gray-400">{t.dashboard.partial}</div>
        </div>

        <div 
          className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border border-gray-200 dark:border-gray-700 cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => onNavigate?.('controls', { status: 'not_applicable' })}
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
              <XCircle className="w-5 h-5 text-gray-500" />
            </div>
          </div>
          <div className="text-3xl font-bold text-gray-500">{status.not_applicable_controls}</div>
          <div className="text-sm text-gray-500 dark:text-gray-400">{t.dashboard.notApplicable}</div>
        </div>

        <div 
          className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl p-5 shadow-sm text-white cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => onNavigate?.('analytics')}
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
              <TrendingUp className="w-5 h-5" />
            </div>
          </div>
          <div className="text-3xl font-bold">{stats.compliance_score}%</div>
          <div className="text-sm text-indigo-100">{t.dashboard.complianceScore}</div>
        </div>
      </div>

      {/* AI Assessment Summary */}
      {data.assessment && (
        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-xl p-6 border border-purple-200 dark:border-purple-800">
          <div className="flex items-center gap-3 mb-4">
            <Brain className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t.dashboard.aiAssessment}</h3>
          </div>
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            {data.assessment.result_summary || t.dashboard.noSummaryAvailable}
          </p>
          {data.assessment.recommendations && data.assessment.recommendations.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-gray-600 dark:text-gray-400">{t.dashboard.recommendations}:</h4>
              {data.assessment.recommendations.map((rec, idx) => (
                <div key={idx} className="flex items-start gap-3 bg-white dark:bg-gray-800 rounded-lg p-3">
                  <span className={`
                    px-2 py-0.5 rounded text-xs font-semibold uppercase
                    ${rec.priority === 'high' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300' : ''}
                    ${rec.priority === 'medium' ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300' : ''}
                    ${rec.priority === 'low' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : ''}
                  `}>
                    {rec.priority === 'high' ? t.dashboard.priorityHigh : rec.priority === 'medium' ? t.dashboard.priorityMedium : t.dashboard.priorityLow}
                  </span>
                  <span className="text-sm text-gray-700 dark:text-gray-300">{rec.recommendation}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Penalty Alerts - Show for regulations with penalty info */}
      {(framework.framework_type === 'regulation' || ['ZIB', 'GDPR', 'NIS2', 'PCI-DSS'].includes(framework.code)) && (
        <PenaltyAlerts 
          controls={controls} 
          framework={framework}
          onControlClick={(control) => {
            setPenaltyControl(control);
          }}
        />
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex gap-6 overflow-x-auto">
          {(['overview', 'soa', 'risks', 'policies'] as const).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`
                py-3 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap
                ${activeTab === tab 
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400' 
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }
              `}
            >
              {tab === 'overview' && t.nav.overview}
              {tab === 'soa' && t.dashboard.soa}
              {tab === 'risks' && t.nav.risks}
              {tab === 'policies' && t.nav.policies}
            </button>
          ))}
          {/* CIS-specific tabs */}
          {isCISFramework && (
            <>
              <button
                onClick={() => setActiveTab('automated')}
                className={`
                  py-3 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap flex items-center gap-2
                  ${activeTab === 'automated' 
                    ? 'border-green-500 text-green-600 dark:text-green-400' 
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }
                `}
              >
                <Terminal className="w-4 h-4" />
                {t.dashboard.automatedChecks}
              </button>
              <button
                onClick={() => setActiveTab('manual')}
                className={`
                  py-3 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap flex items-center gap-2
                  ${activeTab === 'manual' 
                    ? 'border-amber-500 text-amber-600 dark:text-amber-400' 
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }
                `}
              >
                <Hand className="w-4 h-4" />
                {t.dashboard.manualChecks}
              </button>
            </>
          )}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="min-h-[400px]">
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Risks */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <AlertOctagon className="w-5 h-5 text-red-500" /> {t.dashboard.topRisks}
              </h3>
              {data.top_risks.length > 0 ? (
                <div className="space-y-3">
                  {data.top_risks.map(risk => (
                    <div 
                      key={risk.id} 
                      onClick={() => onNavigate?.('risks', { id: risk.id })}
                      className={`
                        p-4 rounded-lg border-l-4 cursor-pointer hover:shadow-md transition-shadow
                        ${risk.risk_level === 'Critical' ? 'border-red-500 bg-red-50 dark:bg-red-900/10' : ''}
                        ${risk.risk_level === 'High' ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/10' : ''}
                        ${risk.risk_level === 'Medium' ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/10' : ''}
                        ${risk.risk_level === 'Low' ? 'border-green-500 bg-green-50 dark:bg-green-900/10' : ''}
                      `}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-gray-900 dark:text-white">{risk.title}</span>
                        <span className={`
                          px-2 py-0.5 rounded text-xs font-semibold
                          ${risk.risk_level === 'Critical' ? 'bg-red-200 dark:bg-red-800 text-red-800 dark:text-red-200' : ''}
                          ${risk.risk_level === 'High' ? 'bg-orange-200 dark:bg-orange-800 text-orange-800 dark:text-orange-200' : ''}
                          ${risk.risk_level === 'Medium' ? 'bg-amber-200 dark:bg-amber-800 text-amber-800 dark:text-amber-200' : ''}
                          ${risk.risk_level === 'Low' ? 'bg-green-200 dark:bg-green-800 text-green-800 dark:text-green-200' : ''}
                        `}>
                          {risk.risk_level}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">{risk.description}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <CheckCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>{t.dashboard.noOpenRisks}</p>
                </div>
              )}
            </div>

            {/* Upcoming Audits */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-indigo-500" /> {t.dashboard.upcomingAudits}
              </h3>
              {data.upcoming_audits.length > 0 ? (
                <div className="space-y-3">
                  {data.upcoming_audits.map(audit => (
                    <div 
                      key={audit.id} 
                      onClick={() => onNavigate?.('audits', { id: audit.id })}
                      className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg cursor-pointer hover:shadow-md transition-shadow">
                      <div className="font-medium text-gray-900 dark:text-white">{audit.title}</div>
                      <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        <span className="capitalize">{audit.audit_type}</span> • {audit.scheduled_date || 'TBD'}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <ClipboardList className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>{t.dashboard.noScheduledAudits}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'soa' && (
          <EnhancedSoATable
            controls={controls}
            frameworkId={framework.id}
            frameworkName={framework.name}
            frameworkType={framework.framework_type}
            onControlsUpdate={loadDashboard}
          />
        )}

        {activeTab === 'risks' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
            {data.top_risks.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {data.top_risks.map(risk => (
                  <div 
                    key={risk.id} 
                    className={`
                      p-5 rounded-xl border-l-4
                      ${risk.risk_level === 'Critical' ? 'border-red-500 bg-red-50 dark:bg-red-900/10' : ''}
                      ${risk.risk_level === 'High' ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/10' : ''}
                      ${risk.risk_level === 'Medium' ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/10' : ''}
                      ${risk.risk_level === 'Low' ? 'border-green-500 bg-green-50 dark:bg-green-900/10' : ''}
                    `}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-gray-900 dark:text-white">{risk.title}</span>
                      <span className={`
                        px-2 py-0.5 rounded text-xs font-semibold
                        ${risk.risk_level === 'Critical' ? 'bg-red-200 dark:bg-red-800 text-red-800 dark:text-red-200' : ''}
                        ${risk.risk_level === 'High' ? 'bg-orange-200 dark:bg-orange-800 text-orange-800 dark:text-orange-200' : ''}
                        ${risk.risk_level === 'Medium' ? 'bg-amber-200 dark:bg-amber-800 text-amber-800 dark:text-amber-200' : ''}
                        ${risk.risk_level === 'Low' ? 'bg-green-200 dark:bg-green-800 text-green-800 dark:text-green-200' : ''}
                      `}>
                        {risk.risk_level}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{risk.description}</p>
                    <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-500">
                      <span className="flex items-center gap-1">
                        <Users className="w-3 h-3" /> {risk.owner || 'Neodređeno'}
                      </span>
                      <span className="capitalize">{risk.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                <AlertOctagon className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg">Nema registrovanih rizika</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'policies' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
            {data.documents.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {data.documents.map(doc => (
                  <div key={doc.id} className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div className="flex items-start gap-3">
                      <FileText className="w-8 h-8 text-indigo-500 flex-shrink-0" />
                      <div>
                        <div className="font-medium text-gray-900 dark:text-white">{doc.title}</div>
                        <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                          <span className="capitalize">{doc.doc_type}</span> • v{doc.version}
                        </div>
                        <span className={`
                          inline-block mt-2 px-2 py-0.5 rounded text-xs font-semibold capitalize
                          ${doc.status === 'approved' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : ''}
                          ${doc.status === 'draft' ? 'bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-300' : ''}
                          ${doc.status === 'review' ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300' : ''}
                        `}>
                          {doc.status}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                <FileText className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg">Nema politika za ovaj framework</p>
              </div>
            )}
          </div>
        )}

        {/* CIS Automated Checks Tab */}
        {activeTab === 'automated' && isCISFramework && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                  <Terminal className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">Automatske Provere</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Kontrole koje se mogu automatski proveriti putem skripti
                  </p>
                </div>
              </div>
              <span className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full text-sm font-medium">
                {controls.filter(c => c.check_type === 'automated' || c.check_type === 'hybrid').length} kontrola
              </span>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              {controls.filter(c => c.check_type === 'automated' || c.check_type === 'hybrid').length > 0 ? (
                <div className="divide-y divide-gray-100 dark:divide-gray-700">
                  {controls.filter(c => c.check_type === 'automated' || c.check_type === 'hybrid').map(control => (
                    <div key={control.id} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-mono text-sm text-indigo-600 dark:text-indigo-400">{control.control_code}</span>
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              control.check_type === 'automated' 
                                ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                                : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                            }`}>
                              {control.check_type === 'automated' ? 'Auto' : 'Hybrid'}
                            </span>
                          </div>
                          <h4 className="font-medium text-gray-900 dark:text-white mb-1">{control.title}</h4>
                          <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">{control.description}</p>
                        </div>
                        <button
                          onClick={() => setSelectedControl(control)}
                          className="ml-4 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium flex items-center gap-2 transition-colors"
                        >
                          <Terminal className="w-4 h-4" />
                          Skripte
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                  <Terminal className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p className="text-lg">Nema automatizovanih kontrola</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* CIS Manual Checks Tab */}
        {activeTab === 'manual' && isCISFramework && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded-lg flex items-center justify-center">
                  <Hand className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">Manualne Provere</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Kontrole koje zahtevaju manualnu proveru i verifikaciju
                  </p>
                </div>
              </div>
              <span className="px-3 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded-full text-sm font-medium">
                {controls.filter(c => c.check_type === 'manual' || !c.check_type).length} kontrola
              </span>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              {controls.filter(c => c.check_type === 'manual' || !c.check_type).length > 0 ? (
                <div className="divide-y divide-gray-100 dark:divide-gray-700">
                  {controls.filter(c => c.check_type === 'manual' || !c.check_type).map(control => (
                    <div key={control.id} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-mono text-sm text-indigo-600 dark:text-indigo-400">{control.control_code}</span>
                            <span className="px-2 py-0.5 rounded text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                              Manual
                            </span>
                          </div>
                          <h4 className="font-medium text-gray-900 dark:text-white mb-1">{control.title}</h4>
                          <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">{control.description}</p>
                        </div>
                        <div className="ml-4 flex items-center gap-2">
                          <span className={`px-3 py-1 rounded-lg text-sm font-medium ${
                            control.implementation_status === 'implemented' 
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                              : control.implementation_status === 'in_progress'
                              ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                          }`}>
                            {control.implementation_status === 'implemented' ? 'Implementirano' :
                             control.implementation_status === 'in_progress' ? 'U toku' : 'Nije započeto'}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                  <Hand className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p className="text-lg">Nema manualnih kontrola</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* CIS Script Viewer Modal */}
      {selectedControl && (
        <CISScriptViewer 
          control={selectedControl} 
          onClose={() => setSelectedControl(null)} 
        />
      )}

      {/* Penalty Control Modal - for regulation penalty details */}
      {penaltyControl && (
        <PenaltyControlModal
          control={penaltyControl}
          framework={framework}
          onClose={() => setPenaltyControl(null)}
        />
      )}
    </div>
  );
};
